/**
 * Prøvesett steg 2 — Vurdering (CX kompositt-veiviser 2026-11-01).
 *
 * Multi-select av komponent-typer (opp til 7) med vekt-input per komponent.
 * Når >1 komponent valgt: modus-velger (weighted vs all_required) + sanntids
 * vekt-sum. Smart default-engine foreslår modus+vekter basert på kombinasjonen.
 *
 * Rubric-dropdown per komponent legges til i Trinn C. For nå: tekst som sier
 * "Vurderingsmatrise velges i neste steg".
 */
import { Clock, Award, BookOpen, FileText, Users, Hand, Microscope, Camera, Folder, GraduationCap } from 'lucide-react';
import type { GradingScale } from '../../../types/exam';
import type { ExamSetWizardData, WizardComponentDraft } from '../NewExamSetWizard';
import { suggestCompositeDefaults, redistributeWeights } from '../../../lib/compositeDefaults';
import SebTerm from '../SebTerm';

export interface AssessmentStepProps {
  data: ExamSetWizardData;
  patch: (updates: Partial<ExamSetWizardData>) => void;
}

const SCALE_LABELS: Record<GradingScale, { label: string; description: string }> = {
  pass_fail: {
    label: 'Bestått / Ikke bestått',
    description: 'Enklest. Egnet for sertifiseringskurs der nivå ikke rapporteres.',
  },
  three_tier: {
    label: 'Meget godt bestått / Bestått / Ikke bestått',
    description: 'Trenivå. Egnet når gradering gir mening pedagogisk.',
  },
  a_f: {
    label: 'A–F (skolekarakter)',
    description: 'Skalalært karaktersystem. Egnet for formelle kurs.',
  },
  percentage: {
    label: 'Prosent (0–100)',
    description: 'Numerisk score. Beståtterskel gir bestått/ikke bestått.',
  },
};

type CompType = WizardComponentDraft['component_type'];

interface CompTypeDef {
  value: CompType;
  label: string | React.ReactNode;
  icon: React.ReactNode;
  description: string | React.ReactNode;
  defaultAnonymous: boolean;
  defaultGraceHours: number;
  defaultMaxFiles: number;
  needsRubric: boolean;
}

const COMPONENT_TYPES: CompTypeDef[] = [
  {
    value: 'written',
    label: <>Skriftlig prøve (<SebTerm variant="bare" />)</>,
    icon: <FileText size={18} />,
    description: <>Flervalgs- eller skrivprøve i <SebTerm variant="bare" />. Spørsmål kreves.</>,
    defaultAnonymous: true,
    defaultGraceHours: 0,
    defaultMaxFiles: 0,
    needsRubric: false,
  },
  {
    value: 'oral',
    label: 'Muntlig eksamen',
    icon: <Users size={18} />,
    description: 'Sensor samtaler med kandidat og registrerer vurdering.',
    defaultAnonymous: false,
    defaultGraceHours: 0,
    defaultMaxFiles: 0,
    needsRubric: true,
  },
  {
    value: 'project',
    label: 'Prosjektoppgave',
    icon: <Folder size={18} />,
    description: 'Kandidat laster opp innlevering (PDF/Word/PPT). Sensor vurderer etter matrise.',
    defaultAnonymous: true,
    defaultGraceHours: 24,
    defaultMaxFiles: 10,
    needsRubric: true,
  },
  {
    value: 'practical',
    label: 'Praktisk demonstrasjon',
    icon: <Hand size={18} />,
    description: 'Kandidat utfører praktisk oppgave. Sensor vurderer etter matrise.',
    defaultAnonymous: false,
    defaultGraceHours: 0,
    defaultMaxFiles: 0,
    needsRubric: true,
  },
  {
    value: 'portfolio',
    label: 'Mappevurdering',
    icon: <GraduationCap size={18} />,
    description: 'Sammenstilling av arbeider over tid. Innleveres som samlet mappe.',
    defaultAnonymous: true,
    defaultGraceHours: 48,
    defaultMaxFiles: 20,
    needsRubric: true,
  },
  {
    value: 'observation',
    label: 'Kontinuerlig observasjon',
    icon: <Microscope size={18} />,
    description: 'Vurdering basert på løpende observasjon gjennom kurset.',
    defaultAnonymous: false,
    defaultGraceHours: 0,
    defaultMaxFiles: 0,
    needsRubric: true,
  },
  {
    value: 'group',
    label: 'Gruppearbeid',
    icon: <Camera size={18} />,
    description: 'Vurdering av gruppeleveranse — individuell karakter krever særskilt vurdering.',
    defaultAnonymous: false,
    defaultGraceHours: 24,
    defaultMaxFiles: 10,
    needsRubric: true,
  },
];

export default function AssessmentStep({ data, patch }: AssessmentStepProps) {
  const selected = data.components;
  const selectedTypes = selected.map(c => c.component_type);
  const nonWritten = !selectedTypes.includes('written');
  const isComposite = selected.length > 1;
  const totalWeight = selected.reduce((s, c) => s + (c.weight_pct || 0), 0);
  const weightValid = !isComposite || data.composite_mode !== 'weighted_composite' || totalWeight === 100;

  function toggleType(def: CompTypeDef, checked: boolean) {
    let newComponents: WizardComponentDraft[];
    if (checked) {
      if (selected.some(c => c.component_type === def.value)) return;
      const draft: WizardComponentDraft = {
        component_type: def.value,
        sequence: selected.length + 1,
        weight_pct: null,
        rubric_id: null,
        is_anonymous: def.defaultAnonymous,
        grace_period_hours: def.defaultGraceHours,
        max_files_per_submission: def.defaultMaxFiles,
        task_description: '',
      };
      newComponents = [...selected, draft];
    } else {
      newComponents = selected
        .filter(c => c.component_type !== def.value)
        .map((c, i) => ({ ...c, sequence: i + 1 }));
    }

    // Smart default (Trinn D-integrering): foreslå modus + vekter basert på ny
    // komposisjon. Bare overstyr hvis bruker ikke har redigert vekter manuelt.
    const anyUserWeightEdited = selected.some(c => c._weight_user_edited);
    const suggestion = suggestCompositeDefaults(newComponents.map(c => c.component_type));
    const nextMode = newComponents.length <= 1 ? null : suggestion.mode;
    const nextComponents = newComponents.length <= 1 || anyUserWeightEdited
      ? newComponents
      : redistributeWeights(newComponents, suggestion);

    // SEB-regel: krev kun når skriftlig er inkludert
    const sebRequired = nextComponents.some(c => c.component_type === 'written');

    patch({
      components: nextComponents,
      composite_mode: nextMode,
      // Backwards-kompat: primary_assessment_type = første komponent, eller 'written' hvis tom
      primary_assessment_type: (nextComponents[0]?.component_type as any) ?? 'written',
      seb_required: sebRequired,
    });
  }

  function setWeight(idx: number, pct: number) {
    const next = selected.map((c, i) =>
      i === idx ? { ...c, weight_pct: pct, _weight_user_edited: true } : c
    );
    patch({ components: next });
  }

  function setMode(mode: 'weighted_composite' | 'all_required_composite') {
    // Når bruker bytter til weighted uten vekter satt, autofordel jevnt
    let next = selected;
    if (mode === 'weighted_composite' && selected.every(c => !c.weight_pct)) {
      const even = Math.floor(100 / selected.length);
      const remainder = 100 - even * selected.length;
      next = selected.map((c, i) => ({
        ...c,
        weight_pct: even + (i === 0 ? remainder : 0),
      }));
    }
    patch({ composite_mode: mode, components: next });
  }

  return (
    <div className="space-y-5">
      {/* Komponent-velger */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          <BookOpen size={14} className="inline mr-1" />
          Vurderingsformer
        </label>
        <p className="text-xs text-kf-gray-600 mb-3">
          Velg én eller flere. Flere komponenter gir en <strong>kompositt vurdering</strong> — alle
          må bestås (eller vektes sammen) for at kandidaten skal få kompetanseattest.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {COMPONENT_TYPES.map((t) => {
            const checked = selectedTypes.includes(t.value);
            return (
              <label
                key={t.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  checked
                    ? 'bg-kf-blue-50 border-kf-blue-400 ring-1 ring-kf-blue-400'
                    : 'bg-white border-kf-gray-200 hover:bg-kf-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => toggleType(t, e.target.checked)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-kf-gray-900 text-sm flex items-center gap-2">
                    <span className="text-kf-blue-700">{t.icon}</span>
                    {t.label}
                  </div>
                  <div className="text-xs text-kf-gray-600 mt-0.5">{t.description}</div>
                </div>
              </label>
            );
          })}
        </div>
        {selected.length === 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            Minst én vurderingsform må velges.
          </div>
        )}
      </div>

      {/* Modus + vekter (kun kompositt) */}
      {isComposite && (
        <div className="rounded-lg border border-kf-blue-200 bg-kf-blue-50 p-4 space-y-3">
          <div className="font-medium text-kf-gray-900 text-sm">Kompositt vurdering ({selected.length} komponenter)</div>

          <div className="flex flex-col sm:flex-row gap-2">
            <label className={`flex items-start gap-2 p-2 rounded border cursor-pointer flex-1 ${
              data.composite_mode === 'weighted_composite' ? 'bg-white border-kf-blue-500 ring-1 ring-kf-blue-500' : 'bg-white border-kf-gray-200'
            }`}>
              <input type="radio" checked={data.composite_mode === 'weighted_composite'}
                onChange={() => setMode('weighted_composite')} className="mt-0.5" />
              <div>
                <div className="font-medium text-sm">Vektet sum</div>
                <div className="text-xs text-kf-gray-600">Hver komponent gir en score — totalscore = vektet sum. Sum må være 100 %.</div>
              </div>
            </label>
            <label className={`flex items-start gap-2 p-2 rounded border cursor-pointer flex-1 ${
              data.composite_mode === 'all_required_composite' ? 'bg-white border-kf-blue-500 ring-1 ring-kf-blue-500' : 'bg-white border-kf-gray-200'
            }`}>
              <input type="radio" checked={data.composite_mode === 'all_required_composite'}
                onChange={() => setMode('all_required_composite')} className="mt-0.5" />
              <div>
                <div className="font-medium text-sm">Alle må bestås</div>
                <div className="text-xs text-kf-gray-600">Hver komponent må bestås separat. Ingen vekting — laveste score blir totalscore.</div>
              </div>
            </label>
          </div>

          {/* Weight-input kun ved weighted */}
          {data.composite_mode === 'weighted_composite' && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-kf-gray-700">Vekting per komponent</div>
              {selected.map((c, idx) => {
                const def = COMPONENT_TYPES.find(t => t.value === c.component_type)!;
                return (
                  <div key={c.component_type} className="flex items-center gap-3 bg-white rounded p-2 border border-kf-gray-200">
                    <span className="text-kf-blue-700">{def.icon}</span>
                    <span className="flex-1 text-sm">{def.label}</span>
                    <input type="number" min={0} max={100} value={c.weight_pct ?? ''}
                      onChange={e => setWeight(idx, Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                      className="w-20 px-2 py-1 border border-kf-gray-300 rounded text-sm text-right" />
                    <span className="text-sm text-kf-gray-600">%</span>
                  </div>
                );
              })}
              <div className={`text-sm font-medium ${totalWeight === 100 ? 'text-emerald-700' : 'text-red-700'}`}>
                Sum: {totalWeight} %{totalWeight !== 100 && ` (må være 100)`}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rubric-forhåndsvisning (velges i neste steg) */}
      {selected.some(c => COMPONENT_TYPES.find(t => t.value === c.component_type)?.needsRubric) && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <strong>Vurderingsmatrise kreves</strong> for:
          <ul className="list-disc ml-5 mt-1">
            {selected.filter(c => COMPONENT_TYPES.find(t => t.value === c.component_type)?.needsRubric)
              .map(c => <li key={c.component_type}>{COMPONENT_TYPES.find(t => t.value === c.component_type)?.label}</li>)}
          </ul>
          <div className="mt-2">Velges i neste steg («Matrise»).</div>
        </div>
      )}

      {/* Tid — gjelder skriftlig */}
      {selectedTypes.includes('written') && (
        <div>
          <label className="block text-sm font-medium text-kf-gray-700 mb-1">
            <Clock size={14} className="inline mr-1" />
            Tidsbegrensning for skriftlig prøve (minutter)
          </label>
          <input
            type="number"
            value={data.time_limit_minutes ?? ''}
            onChange={(e) =>
              patch({ time_limit_minutes: e.target.value ? parseInt(e.target.value) : null })
            }
            placeholder="Ingen grense"
            min={1}
            max={600}
            className="w-32 px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          />
          <span className="text-xs text-kf-gray-500 ml-2">Tom = ubegrenset tid</span>
        </div>
      )}
      {nonWritten && selected.length > 0 && (
        <div className="text-xs text-kf-gray-500 italic">
          Ingen tidsbegrensning for ikke-skriftlige vurderinger — tid settes direkte av sensor/kursarrangør.
        </div>
      )}

      {/* Beståttgrense (på prøvesett-nivå — gjelder skriftlig; sensor kan overstyre per komponent) */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          <Award size={14} className="inline mr-1" />
          Beståttgrense (%)
        </label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            value={data.pass_threshold}
            onChange={(e) => patch({ pass_threshold: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
            min={0}
            max={100}
            className="w-24 px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          />
          <span className="text-sm text-kf-gray-600">
            Minimum {data.pass_threshold}% riktig for bestått
          </span>
        </div>
      </div>

      {/* Karakterskala */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-2">
          <BookOpen size={14} className="inline mr-1" />
          Karakterskala
        </label>
        <div className="space-y-2">
          {(Object.keys(SCALE_LABELS) as GradingScale[]).map((scale) => (
            <label
              key={scale}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                data.grading_scale === scale
                  ? 'bg-kf-blue-50 border-kf-blue-400 ring-1 ring-kf-blue-400'
                  : 'bg-white border-kf-gray-200 hover:bg-kf-gray-50'
              }`}
            >
              <input
                type="radio"
                name="grading-scale"
                value={scale}
                checked={data.grading_scale === scale}
                onChange={() => patch({ grading_scale: scale })}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-kf-gray-900 text-sm">{SCALE_LABELS[scale].label}</div>
                <div className="text-xs text-kf-gray-600 mt-0.5">{SCALE_LABELS[scale].description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {!weightValid && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900">
          Vektene summerer ikke til 100. Juster før du går videre.
        </div>
      )}
    </div>
  );
}
